import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronRight } from 'lucide-react';
import BinGridPreview from '../BinGridPreview/BinGridPreview';
import PartSearchView from './PartSearchView';
import SettingsMenu from './SettingsMenu';

const FacilityBinPickingSimulation = () => {
  const [bins, setBins] = useState(() => {
    const savedBins = localStorage.getItem('calibratedBins');
    return savedBins ? JSON.parse(savedBins) : [];
  });
  const [currentOrder, setCurrentOrder] = useState([]);
  const [isOrderActive, setIsOrderActive] = useState(false);
  const [calibrationMode, setCalibrationMode] = useState('none');
  const [calibrationStep, setCalibrationStep] = useState(0);
  const [gridCorners, setGridCorners] = useState({
    topLeft: { x: 100, y: 100 },
    topRight: { x: 600, y: 100 },
    bottomLeft: { x: 100, y: 500 },
    bottomRight: { x: 600, y: 500 },
  });
  const [draggedCorner, setDraggedCorner] = useState(null);
  const [rows, setRows] = useState(5);
  const [columns, setColumns] = useState(10);
  const [calibrationAnimationIndex, setCalibrationAnimationIndex] = useState(-1);
  const [isFlashingState, setIsFlashingState] = useState(false);
  const [mode, setMode] = useState('workOrder');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const resultsRef = useRef(null);
  const [selectedBolt, setSelectedBolt] = useState(null);
  const inputRef = useRef(null);
  const [showQuantities, setShowQuantities] = useState(true);
  const [showInventory, setShowInventory] = useState(false);
  const [inventory, setInventory] = useState({});
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [customCsvData, setCustomCsvData] = useState(null);
  const [isCustomCsvActive, setIsCustomCsvActive] = useState(false);
  const [csvUploadError, setCsvUploadError] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const fileInputRef = useRef(null);
  const [showBinGridPreview, setShowBinGridPreview] = useState(false);
  const [mobileSelectedPart, setMobileSelectedPart] = useState(null);
  const [lastMobileSelectionTime, setLastMobileSelectionTime] = useState(null);

  const [binLightsDemoActive, setBinLightsDemoActive] = useState(false);
  const [binLightsDemoPhase, setBinLightsDemoPhase] = useState('wave');
  const [binLightsDemoIndex, setBinLightsDemoIndex] = useState(0);
  const [binLightsDemoColors, setBinLightsDemoColors] = useState({});

  // Generate or retrieve session ID for desktop instance
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('sessionId');
    if (existing) {
      console.log('(FACILITY) Using existing sessionId:', existing);
      return existing;
    }
    const newId = Math.random().toString(36).substring(2, 10);
    console.log('(FACILITY) Generated new sessionId:', newId);
    localStorage.setItem('sessionId', newId);
    return newId;
  });

  const binLookup = useMemo(() => {
    const lookup = {};
    bins.forEach(bin => { lookup[bin.id] = bin; });
    return lookup;
  }, [bins]);

  const lastSelectionTimeRef = useRef(lastMobileSelectionTime);
  const mobileSelectedPartRef = useRef(mobileSelectedPart);

  useEffect(() => {
    lastSelectionTimeRef.current = lastMobileSelectionTime;
  }, [lastMobileSelectionTime]);

  useEffect(() => {
    mobileSelectedPartRef.current = mobileSelectedPart;
  }, [mobileSelectedPart]);

  const pollIntervalRef = useRef(200);
  const consecutiveEmptyResultsRef = useRef(0);

  const getApiBaseUrl = useCallback(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost ? `${window.location.protocol}//${window.location.host}` : window.location.origin;
  }, []);

  const checkForMobileSelections = useCallback(async () => {
    // console.log(`(FACILITY) Polling for session ID: ${sessionId}`);
    try {
      const apiUrl = `${getApiBaseUrl()}/api/selected-part?sessionId=${encodeURIComponent(sessionId)}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1500);
      
      const response = await fetch(apiUrl, { 
        signal: controller.signal,
        headers: { 'Cache-Control': 'no-cache' }
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        // console.log('(FACILITY) Data from /api/selected-part:', JSON.stringify(data));

        if (data.hasSelection) {
          if (data.sessionId && data.sessionId !== sessionId) {
            console.warn(`(FACILITY) Session ID mismatch! Polled for ${sessionId}, got data for ${data.sessionId}. Ignoring.`);
            return;
          }

          if (!lastSelectionTimeRef.current || data.timestamp !== lastSelectionTimeRef.current) {
            const bolt = data.selection;
            console.log('(FACILITY) Received bolt for highlighting:', JSON.stringify(bolt));
            const binId = parseInt(bolt['Bin Number']);
            console.log(`(FACILITY) Calculated binId for highlight: ${binId}`);

            const binToHighlight = binLookup[binId];
            // console.log('(FACILITY) Bin from binLookup based on binId:', JSON.stringify(binToHighlight));

            pollIntervalRef.current = 200;
            consecutiveEmptyResultsRef.current = 0;
            
            ReactDOM.unstable_batchedUpdates(() => {
              // console.log('(FACILITY) Updating states: setMobileSelectedPart, setLastMobileSelectionTime, setSelectedBolt');
              setMobileSelectedPart(bolt);
              setLastMobileSelectionTime(data.timestamp);
              setSelectedBolt(bolt);
              
              if (binToHighlight) {
                // console.log(`(FACILITY) Highlighting binId: ${binId} by updating bins state.`);
                setBins(prevBins => {
                  const newBins = prevBins.map(b => ({ ...b, isHighlighted: b.id === binId }));
                  // const highlightedBinInNewState = newBins.find(b => b.isHighlighted);
                  // console.log('(FACILITY) Highlighted bin in new state:', JSON.stringify(highlightedBinInNewState));
                  return newBins;
                });
              } else if (bins.length > 0) {
                console.warn(`(FACILITY) BinId ${binId} not in binLookup. Attempting fallback to closest bin.`);
                let closestBin = bins[0];
                let minDiff = Math.abs(closestBin.id - binId);
                for (let i = 1; i < bins.length; i++) {
                  const diff = Math.abs(bins[i].id - binId);
                  if (diff < minDiff) {
                    minDiff = diff;
                    closestBin = bins[i];
                  }
                }
                console.log(`(FACILITY) Fallback: Highlighting closest binId: ${closestBin.id}`);
                setBins(prevBins => prevBins.map(b => ({ ...b, isHighlighted: b.id === closestBin.id })));
              } else {
                console.warn('(FACILITY) No bins available to highlight.');
              }
            });
          } else {
            // console.log('(FACILITY) Timestamp matches, no state update needed for mobile selection.');
          }
        } else if (mobileSelectedPartRef.current) {
          // console.log('(FACILITY) API reports no selection for this session, clearing existing highlight on desktop.');
          ReactDOM.unstable_batchedUpdates(() => {
            setMobileSelectedPart(null);
            setLastMobileSelectionTime(null);
            setSelectedBolt(null);
            setBins(prevBins => prevBins.map(b => ({ ...b, isHighlighted: false })));
          });
          pollIntervalRef.current = 200; 
          consecutiveEmptyResultsRef.current = 0;
        } else {
          // console.log('(FACILITY) No selection reported by API, and no current mobile selection on desktop.');
          consecutiveEmptyResultsRef.current++;
          if (consecutiveEmptyResultsRef.current > 5) {
            pollIntervalRef.current = Math.min(1500, pollIntervalRef.current + 200);
          }
        }
      } else {
        console.error(`(FACILITY) API error when polling /api/selected-part: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('(FACILITY) Error checking for mobile selections:', error);
      }
      pollIntervalRef.current = Math.min(2000, pollIntervalRef.current + 100);
    }
  }, [bins, binLookup, getApiBaseUrl, sessionId, setMobileSelectedPart, setLastMobileSelectionTime, setSelectedBolt, setBins]);

  useEffect(() => {
    if (selectedBolt) {
      // console.log(`(FACILITY) selectedBolt state updated: Bin Number ${selectedBolt['Bin Number']}, Part: ${selectedBolt['Bolt Size']}. Current mode: ${mode}`);
    } else {
      // console.log('(FACILITY) selectedBolt state updated to null.');
    }
  }, [selectedBolt, mode]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const poll = async () => {
      if (!isMounted) return;
      await checkForMobileSelections();
      if (isMounted) {
        timeoutId = setTimeout(poll, pollIntervalRef.current);
      }
    };

    const initialDelayTimeoutId = setTimeout(poll, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      clearTimeout(initialDelayTimeoutId);
    };
  }, [checkForMobileSelections]);

  const generateInitialInventory = useCallback(() => {
    const newInventory = {};
    bins.forEach(bin => {
      newInventory[bin.id] = Math.floor(Math.random() * 20) + 11;
    });
    setInventory(newInventory);
  }, [bins]);
  
  useEffect(() => {
    generateInitialInventory();
  }, [generateInitialInventory]);

  useEffect(() => {
    localStorage.setItem('calibratedBins', JSON.stringify(bins));
  }, [bins]);

  useEffect(() => {
    if (mode === 'partSearch' && query.length > 0) {
      const fetchData = async () => {
        try {
          let url = '/api/search';
          let options = {};

          if (isCustomCsvActive && customCsvData && customCsvData.length > 0) {
            options = {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ query, customGridData: customCsvData }), 
            };
          } else {
            url = `/api/search?q=${encodeURIComponent(query)}`;
            options = { method: 'GET' };
          }

          const response = await fetch(url, options);
          if (!response.ok) {
            const errorText = await response.text();
            console.error('(App) Error response body:', errorText);
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
          }
          const data = await response.json();
          setResults(data);
        } catch (error) {
          console.error('Error fetching data:', error);
          setResults([]);
        }
      };
      fetchData();
    } else {
      setResults([]);
    }
  }, [query, mode, isCustomCsvActive, customCsvData]);

  useEffect(() => {
    if (mode === 'workOrder') {
      setSelectedBolt(null);
      setBins((prevBins) => prevBins.map((bin) => ({ ...bin, isHighlighted: false })));
    }
  }, [mode]);

  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  useEffect(() => {
    if (calibrationAnimationIndex >= 0 && calibrationAnimationIndex < bins.length) {
      const timer = setTimeout(() => {
        setCalibrationAnimationIndex(calibrationAnimationIndex + 1);
      }, 50);
      return () => clearTimeout(timer);
    } else if (calibrationAnimationIndex === bins.length) {
      setCalibrationAnimationIndex(-1);
      setIsFlashingState(true);
      setTimeout(() => {
        setIsFlashingState(false);
      }, 800);
    }
  }, [calibrationAnimationIndex, bins.length]);

  useEffect(() => {
    if (!binLightsDemoActive || bins.length === 0) return;

    const timer = setTimeout(() => {
      if (binLightsDemoPhase === 'wave') {
        if (binLightsDemoIndex < bins.length) {
          const newColors = {};
          for (let i = 0; i < bins.length; i++) {
            const distance = Math.abs(i - binLightsDemoIndex);
            if (distance <= 3) {
              const intensity = 1 - (distance / 3);
              newColors[bins[i].id] = {
                color: `rgba(${Math.floor(180 + 75 * intensity)}, ${Math.floor(220 + 35 * intensity)}, 255, ${0.7 + intensity * 0.3})`,
                border: `rgba(100, 255, 255, ${0.8 + intensity * 0.2})`
              };
            }
          }
          setBinLightsDemoColors(newColors);
          setBinLightsDemoIndex(binLightsDemoIndex + 1);
        } else {
          setBinLightsDemoPhase('rainbow');
          setBinLightsDemoIndex(0);
        }
      } else if (binLightsDemoPhase === 'rainbow') {
        if (binLightsDemoIndex < 60) {
          const newColors = {};
          bins.forEach((bin, index) => {
            const hue = ((binLightsDemoIndex * 6 + index * 30) % 360);
            newColors[bin.id] = {
              color: `hsla(${hue}, 100%, 75%, 0.8)`,
              border: `hsl(${hue}, 100%, 60%)`
            };
          });
          setBinLightsDemoColors(newColors);
          setBinLightsDemoIndex(binLightsDemoIndex + 1);
        } else {
          setBinLightsDemoPhase('pulse');
          setBinLightsDemoIndex(0);
        }
      } else if (binLightsDemoPhase === 'pulse') {
        if (binLightsDemoIndex < 30) {
          const intensity = 0.5 + 0.5 * Math.sin((binLightsDemoIndex / 30) * Math.PI * 4);
          const newColors = {};
          bins.forEach(bin => {
            newColors[bin.id] = {
              color: `rgba(${Math.floor(220 + 35 * intensity)}, ${Math.floor(150 + 105 * intensity)}, 255, ${0.6 + intensity * 0.4})`,
              border: `rgba(255, ${Math.floor(100 + 155 * intensity)}, 255, ${0.7 + intensity * 0.3})`
            };
          });
          setBinLightsDemoColors(newColors);
          setBinLightsDemoIndex(binLightsDemoIndex + 1);
        } else {
          setBinLightsDemoPhase('final');
          setBinLightsDemoIndex(0);
        }
      } else if (binLightsDemoPhase === 'final') {
        if (binLightsDemoIndex < 40) {
          const newColors = {};
          bins.forEach((bin, index) => {
            const isActive = Math.random() < 0.3;
            if (isActive) {
              const colors = ['rgba(255, 100, 100, 0.9)', 'rgba(100, 255, 200, 0.9)', 'rgba(120, 180, 255, 0.9)', 
                             'rgba(255, 220, 100, 0.9)', 'rgba(200, 120, 255, 0.9)', 'rgba(100, 255, 255, 0.9)', 
                             'rgba(255, 180, 255, 0.9)', 'rgba(150, 200, 255, 0.9)'];
              const borderColors = ['#ff6464', '#64ffc8', '#78b4ff', '#ffdc64', '#c878ff', '#64ffff', '#ffb4ff', '#96c8ff'];
              const colorIndex = Math.floor(Math.random() * colors.length);
              newColors[bin.id] = {
                color: colors[colorIndex],
                border: borderColors[colorIndex]
              };
            }
          });
          setBinLightsDemoColors(newColors);
          setBinLightsDemoIndex(binLightsDemoIndex + 1);
        } else {
          setBinLightsDemoActive(false);
          setBinLightsDemoPhase('wave');
          setBinLightsDemoIndex(0);
          setBinLightsDemoColors({});
        }
      }
    }, binLightsDemoPhase === 'wave' ? 100 : binLightsDemoPhase === 'rainbow' ? 80 : binLightsDemoPhase === 'pulse' ? 120 : 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binLightsDemoActive, binLightsDemoPhase, binLightsDemoIndex, bins.length]);

  const generateOrder = () => {
    const newOrder = bins
      .filter(() => Math.random() > 0.7) 
      .map((bin) => ({
        id: bin.id,
        quantity: Math.floor(Math.random() * 5) + 1, 
      }));
    setCurrentOrder(newOrder);
    setIsOrderActive(true);
  };

  const completeOrder = () => {
    setInventory(prevInventory => {
      const newInventory = { ...prevInventory };
      currentOrder.forEach(item => {
        newInventory[item.id] = Math.max(0, newInventory[item.id] - item.quantity);
      });
      return newInventory;
    });
    setCurrentOrder([]);
    setIsOrderActive(false);
  };

  const startCalibration = useCallback(() => {
    setCalibrationMode('grid');
    setCalibrationStep(1);
    setIsSettingsOpen(false);
    setMode('workOrder');
  }, []);

  const clearCalibration = useCallback(() => {
    localStorage.removeItem('calibratedBins');
    setBins([]);
    setGridCorners({
      topLeft: { x: 100, y: 100 },
      topRight: { x: 600, y: 100 },
      bottomLeft: { x: 100, y: 500 },
      bottomRight: { x: 600, y: 500 },
    });
    setRows(5);
    setColumns(10);
    setCalibrationAnimationIndex(-1);
    setIsFlashingState(false);
    setCurrentOrder([]);
    setIsOrderActive(false);
    setIsSettingsOpen(false);
    setMode('workOrder');
  }, []);

  const startBinLightsDemo = useCallback(() => {
    if (bins.length === 0) {
      alert('No bins found. Please calibrate the system first.');
      return;
    }
    
    setBinLightsDemoPhase('wave');
    setBinLightsDemoIndex(0);
    setBinLightsDemoColors({});
    
    setBinLightsDemoActive(true);
    setIsSettingsOpen(false);
  }, [bins.length]);

  const handleCornerDrag = useCallback(
    (e, corner) => {
      if (calibrationMode !== 'grid' || calibrationStep !== 1) return;
      const rect = e.target.closest('.projector-view').getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setGridCorners((prevCorners) => ({
        ...prevCorners,
        [corner]: { x, y },
      }));
    },
    [calibrationMode, calibrationStep]
  );

  const computeBinPositions = () => {
    const newBins = [];
    let idCounter = 1;
    const gridLinesX = [];
    const gridLinesY = [];
    for (let col = 0; col <= columns; col++) {
      const tCol = col / columns;
      const xTop = (1 - tCol) * gridCorners.topLeft.x + tCol * gridCorners.topRight.x;
      const xBottom = (1 - tCol) * gridCorners.bottomLeft.x + tCol * gridCorners.bottomRight.x;
      gridLinesX[col] = { xTop, xBottom };
    }
    for (let row = 0; row <= rows; row++) {
      const tRow = row / rows;
      const yLeft = (1 - tRow) * gridCorners.topLeft.y + tRow * gridCorners.bottomLeft.y;
      const yRight = (1 - tRow) * gridCorners.topRight.y + tRow * gridCorners.bottomRight.y;
      gridLinesY[row] = { yLeft, yRight };
    }
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const x1 = gridLinesX[col].xTop * (1 - row / rows) + gridLinesX[col].xBottom * (row / rows);
        const x2 = gridLinesX[col + 1].xTop * (1 - row / rows) + gridLinesX[col + 1].xBottom * (row / rows);
        const y1 = gridLinesY[row].yLeft * (1 - col / columns) + gridLinesY[row].yRight * (col / columns);
        const y2 = gridLinesY[row + 1].yLeft * (1 - col / columns) + gridLinesY[row + 1].yRight * (col / columns);
        newBins.push({
          id: idCounter++,
          position: { x: x1, y: y1 },
          size: { width: x2 - x1, height: y2 - y1 },
          isHighlighted: false,
        });
      }
    }
    setBins(newBins);
    setCalibrationMode('none');
    setCalibrationStep(0);
    setCalibrationAnimationIndex(0);
  };

  const handleBoltSelection = useCallback((bolt) => {
    setSelectedBolt(bolt);
    const binId = parseInt(bolt['Bin Number']);
    const binToHighlight = bins.find((b) => b.id === binId);
    if (binToHighlight) {
      setBins((prevBins) => prevBins.map((b) => b.id === binToHighlight.id ? { ...b, isHighlighted: true } : { ...b, isHighlighted: false }));
    } else {
      console.warn('Bin not found for bolt:', bolt);
      if (bins.length > 0) {
        const sortedBins = [...bins].sort((a, b) => a.id - b.id);
        const closestBin = sortedBins.reduce((closest, b) => Math.abs(b.id - binId) < Math.abs(closest.id - binId) ? b : closest, sortedBins[0]);
        setBins((prevBins) => prevBins.map((b) => b.id === closestBin.id ? { ...b, isHighlighted: true } : { ...b, isHighlighted: false }));
      } else {
        alert('No bins found. Please calibrate the system first.');
      }
    }
  }, [bins]);

  const handleKeyDown = (event) => {
    const filteredResults = sortResults(results);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prevIndex) => prevIndex < filteredResults.length - 1 ? prevIndex + 1 : prevIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
    } else if (event.key === 'Enter') {
      if (selectedIndex >= 0 && selectedIndex < filteredResults.length) {
        handleBoltSelection(filteredResults[selectedIndex]);
      } else if (filteredResults.length === 1) {
        handleBoltSelection(filteredResults[0]);
      }
    }
  };

  const sortResults = (results) => {
    if (!Array.isArray(results) || results.length === 0) return [];
    return [...results].sort((a, b) => (a['Bolt Size'] || '').localeCompare(b['Bolt Size'] || ''));
  };

  const handleBinGridConfirm = useCallback(async (confirmedGridData) => {
    setCustomCsvData(confirmedGridData);
    setIsCustomCsvActive(true);
    setShowBinGridPreview(false);
    setCsvUploadError(null);
    if (isSettingsOpen) setIsSettingsOpen(false);

    try {
      const apiUrl = `${getApiBaseUrl()}/api/set-active-data`;
      await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gridData: confirmedGridData }),
      });
      console.log('(FACILITY) Confirmed custom data sent to backend.');
    } catch (error) {
      console.error('(FACILITY) Error sending active custom data to backend:', error);
    }
  }, [isSettingsOpen, getApiBaseUrl]);

  const handleBinGridCancel = useCallback(() => {
    setShowBinGridPreview(false); 
    setSelectedFileName(''); 
  }, []);

  useEffect(() => {
    const fetchInitialActiveData = async () => {
      if (!isCustomCsvActive) {
        try {
          const apiUrl = `${getApiBaseUrl()}/api/get-active-data`;
          const response = await fetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            if (data.activeCustomData && Array.isArray(data.activeCustomData)) {
              console.log('(FACILITY) Fetched active custom data from backend:', data.activeCustomData);
              setCustomCsvData(data.activeCustomData);
              setIsCustomCsvActive(true);
            }
          }
        } catch (error) {
          console.error('(FACILITY) Error fetching initial active custom data:', error);
        }
      }
    };
    fetchInitialActiveData();
  }, [isCustomCsvActive, getApiBaseUrl]);

  const ProjectorView = () => {
    if (calibrationMode === 'grid' && calibrationStep === 1) {
      return (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-black projector-view"
          onMouseMove={(e) => draggedCorner && handleCornerDrag(e, draggedCorner)}
          onMouseUp={() => setDraggedCorner(null)}
        >
          <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
            <polygon
              points={`${gridCorners.topLeft.x},${gridCorners.topLeft.y} ${gridCorners.topRight.x},${gridCorners.topRight.y} ${gridCorners.bottomRight.x},${gridCorners.bottomRight.y} ${gridCorners.bottomLeft.x},${gridCorners.bottomLeft.y}`}
              style={{ fill: 'rgba(255, 255, 255, 0.1)', stroke: 'red', strokeWidth: 2 }}
            />
          </svg>
          {['topLeft', 'topRight', 'bottomLeft', 'bottomRight'].map((corner) => (
            <div
              key={corner}
              style={{
                position: 'absolute',
                left: `${gridCorners[corner].x - 8}px`,
                top: `${gridCorners[corner].y - 8}px`,
                width: '16px',
                height: '16px',
                backgroundColor: 'red',
                cursor: 'move',
              }}
              onMouseDown={() => setDraggedCorner(corner)}
              onMouseUp={() => setDraggedCorner(null)}
            />
          ))}
          <div className="absolute bottom-4 right-4">
            <button
              onClick={() => setCalibrationStep(2)}
              className="flex items-center px-4 py-2 rounded-lg text-white font-semibold bg-orange-500 hover:bg-orange-600"
            >
              <ChevronRight className="mr-2" size={16} />
              Next
            </button>
          </div>
        </div>
      );
    }
    if (calibrationMode === 'grid' && calibrationStep === 2) {
      return (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black flex items-center justify-end projector-view">
          <div className="bg-white p-8 rounded-lg m-4 w-80">
            <h2 className="text-2xl font-bold mb-4">Enter Grid Dimensions</h2>
            <div className="mb-4">
              <label className="block text-lg font-semibold mb-2">Number of Rows:</label>
              <input type="number" value={rows} onChange={(e) => setRows(parseInt(e.target.value))} className="w-full p-2 border rounded-lg" />
            </div>
            <div className="mb-4">
              <label className="block text-lg font-semibold mb-2">Number of Columns:</label>
              <input type="number" value={columns} onChange={(e) => setColumns(parseInt(e.target.value))} className="w-full p-2 border rounded-lg" />
            </div>
            <button onClick={computeBinPositions} className="flex items-center px-4 py-2 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700">
              <Check className="mr-2" size={16} />
              Complete Calibration
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        <style>
          {`
            @keyframes dissipate {
              0% { transform: scale(1); opacity: 1; background-color: rgba(0, 255, 255, 0.8); }
              100% { transform: scale(0.8); opacity: 0; background-color: rgba(0, 255, 255, 0); }
            }
          `}
        </style>
        <div className={`fixed top-0 left-0 ${mode === 'partSearch' ? 'right-80' : 'right-0'} bottom-0 bg-black projector-view`}>
          {bins.map((bin, index) => {
            const orderItem = currentOrder.find((item) => item.id === bin.id);
            const isHighlighted = bin.isHighlighted || (mode === 'workOrder' && isOrderActive && orderItem) || (mode === 'partSearch' && selectedBolt && bin.id === parseInt(selectedBolt['Bin Number']));
            const isDimmed = (mode === 'workOrder' && isOrderActive && !orderItem) || (mode === 'partSearch' && selectedBolt && bin.id !== parseInt(selectedBolt['Bin Number']));
            const isCalibrating = calibrationAnimationIndex >= 0 && index <= calibrationAnimationIndex;
            
            const demoColor = binLightsDemoColors[bin.id];
            const isDemoActive = binLightsDemoActive && demoColor;
            
            const inventoryLevel = inventory[bin.id] || 0;
            const inventoryColor = showInventory ? (inventoryLevel < 5 ? 'red' : inventoryLevel < 10 ? 'orange' : 'white') : 'white';
            const minDimension = Math.min(bin.size.width, bin.size.height);
            const fontSize = Math.max(32, minDimension * 0.7);
            
            let backgroundColor = 'transparent';
            let borderColor = inventoryColor;
            
            if (isDemoActive) {
              backgroundColor = demoColor.color;
              borderColor = demoColor.border;
            } else if (isHighlighted) {
              backgroundColor = 'rgba(255, 255, 0, 0.6)';
              borderColor = 'yellow';
            } else if (isCalibrating) {
              backgroundColor = 'rgba(0, 255, 0, 0.5)';
              borderColor = 'green';
            } else {
              borderColor = inventoryColor;
            }
            
            return (
              <div
                key={bin.id}
                style={{
                  position: 'absolute',
                  left: `${bin.position.x}px`,
                  top: `${bin.position.y}px`,
                  width: `${bin.size.width}px`,
                  height: `${bin.size.height}px`,
                  border: `2px solid ${borderColor}`,
                  backgroundColor: backgroundColor,
                  opacity: isDimmed && !isDemoActive ? 0.1 : 1,
                  transition: isDemoActive ? 'all 0.1s ease-in-out' : 'background-color 0.2s, opacity 0.2s',
                  animationName: isFlashingState ? 'dissipate' : 'none',
                  animationDuration: '1.5s',
                  animationTimingFunction: 'ease-in-out',
                  animationIterationCount: isFlashingState ? 1 : 'initial',
                  animationFillMode: 'forwards',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: `${fontSize}px`,
                  fontWeight: 'bold',
                  color: 'white',
                  transform: isDemoActive && binLightsDemoPhase === 'pulse' ? `scale(${1 + Math.sin(Date.now() / 200) * 0.1})` : 'scale(1)',
                }}
              >
                {showQuantities && mode === 'workOrder' && isOrderActive && orderItem && <span style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>{orderItem.quantity}</span>}
                {showInventory && !isOrderActive && (
                  <>
                    <span style={{ fontSize: `${fontSize * 0.3}px`, color: 'white' }}>Inventory:</span>
                    <span style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)', color: inventoryColor }}>{inventoryLevel}</span>
                  </>
                )}
              </div>
            );
          })}
          {mobileSelectedPart && (
            <div className="absolute top-4 left-4 bg-white/90 p-4 rounded-lg shadow-lg max-w-xs">
              <h3 className="text-lg font-bold text-blue-800 mb-2">Mobile Selection</h3>
              <p className="font-medium text-gray-800">Part: <span className="text-blue-600">{mobileSelectedPart['Bolt Size']}</span></p>
              <p className="font-medium text-gray-800">Bin: <span className="text-blue-600">{mobileSelectedPart['Bin Number']}</span></p>
            </div>
          )}
          <div className="absolute top-4 right-4 flex flex-col space-y-2">
            {mode === 'workOrder' && (
              <>
                <button onClick={() => generateOrder()} disabled={isOrderActive} className={`flex items-center px-4 py-2 rounded-full text-white font-semibold ${isOrderActive ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} transition duration-150`}>Generate New Work Order</button>
                {isOrderActive && (
                  <div className="bg-white p-4 rounded-lg shadow-md max-w-sm w-full">
                    <h3 className="font-bold text-xl mb-4 text-gray-800 border-b pb-2">Current Order</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {currentOrder.map((item) => <div key={item.id} className="bg-gray-100 p-2 rounded flex justify-between items-center"><span className="font-medium text-gray-700">Bin {item.id}:</span><span className="text-blue-600 font-bold">{item.quantity}</span></div>)}
                    </div>
                  </div>
                )}
                <button onClick={() => completeOrder()} disabled={!isOrderActive} className={`flex items-center px-4 py-2 rounded-full text-white font-semibold ${!isOrderActive ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} transition duration-150`}>Complete Order</button>
              </>
            )}
          </div>
          {mode === 'workOrder' && (
            <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
              <button onClick={() => setMode('partSearch')} className="flex items-center px-4 py-2 rounded-full text-white font-semibold bg-blue-600 hover:bg-blue-700 transition duration-150">Switch to Part Search Mode</button>
              <button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className="flex items-center px-4 py-2 rounded-full text-white font-semibold bg-gray-600 hover:bg-gray-700 transition duration-150">{isSettingsOpen ? 'Close Settings' : 'Open Settings'}</button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="container mx-auto p-4 bg-gray-100 min-h-screen">
      {mode === 'partSearch' && <PartSearchView 
        query={query}
        results={results}
        selectedIndex={selectedIndex}
        selectedBolt={selectedBolt}
        inputRef={inputRef}
        resultsRef={resultsRef}
        sessionId={sessionId}
        onQueryChange={(e) => {
          setQuery(e.target.value);
          setSelectedIndex(-1);
          setSelectedBolt(null);
          setBins((prevBins) => prevBins.map((bin) => ({ ...bin, isHighlighted: false })));
        }}
        onKeyDown={handleKeyDown}
        onBoltSelected={handleBoltSelection}
        onClearSelection={() => {
          setSelectedBolt(null);
          setBins((prevBins) => prevBins.map((bin) => ({ ...bin, isHighlighted: false })));
          setQuery('');
        }}
        mode={mode}
        setMode={setMode}
        isSettingsOpen={isSettingsOpen}
        setIsSettingsOpen={setIsSettingsOpen}
      />}
      {<ProjectorView />}
      <SettingsMenu 
        // Core settings props
        isSettingsOpen={isSettingsOpen}
        onSetIsSettingsOpen={setIsSettingsOpen}
        calibrationMode={calibrationMode}
        onStartCalibration={startCalibration}
        onClearCalibration={clearCalibration}
        
        // Display toggles
        showQuantities={showQuantities}
        onToggleShowQuantities={() => setShowQuantities(!showQuantities)}
        showInventory={showInventory}
        onToggleShowInventory={() => setShowInventory(!showInventory)}
        
        // CSV upload props
        fileInputRef={fileInputRef}
        selectedFileName={selectedFileName}
        isCustomCsvActive={isCustomCsvActive}
        csvUploadError={csvUploadError}
        
        // Demo function
        onStartBinLightsDemo={startBinLightsDemo}
        
        // Internal state setters (for CSV functionality)
        setSelectedFileName={setSelectedFileName}
        setCsvUploadError={setCsvUploadError}
        setIsCustomCsvActive={setIsCustomCsvActive}
        setCustomCsvData={setCustomCsvData}
        setShowBinGridPreview={setShowBinGridPreview}
        getApiBaseUrl={getApiBaseUrl}
      />

      <BinGridPreview 
        initialBinData={customCsvData} 
        onConfirm={handleBinGridConfirm}
        onCancel={handleBinGridCancel}
        isVisible={showBinGridPreview}
      />
    </div>
  );
};

export default FacilityBinPickingSimulation; 